import {
  addCompanyUser,
  deleteCompanyUser,
  getCompanyUsers,
  updateCompanyUser,
  batchCreateCompanyUsers,
  resetCompanyUserPassword,
} from "../actions/company.user.action";
import TableList from "../components/TableList";
import { CompanyUser, CompanyUserType } from "../models/company.user.model";
import { useEffect, useRef } from "react";
import CompanyUserForm from "./CompanyUserForm";
import ResetPasswordForm from "./ResetPasswordForm";
import { filterOnKeys, titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import { getCompanies } from "../actions/company.action";
import { Chip } from "@mui/material";
import { CheckCircleOutline, HighlightOff, LockReset } from "@mui/icons-material";
import { green, red } from "@mui/material/colors";
import { Company } from "../models/company.model";
import LoadingButton from "../components/LoadingButton";
import CompanyUserImportForm from "./CompanyUserImportForm";
import { sleep } from "../lib/helper";

const user_type_color_map: any = {
  root: "primary",
  admin: "success",
  viewer: "info",
};

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    label: "Name",
  },
  {
    id: "email",
    label: "Email",
    render: (v, r: CompanyUser) => <span>{~(v || "").indexOf(r.companyId) ? "" : v}</span>,
  },
  {
    id: "userId",
    label: "Store Id",
  },
  {
    id: "type",
    label: "User Type",
    render: (v) => <Chip color={user_type_color_map[v]} label={titleStr(v)} />,
  },
  {
    id: "enabled",
    label: "Enabled",
    render: (value) => (value ? <CheckCircleOutline sx={{ color: green[500] }} /> : <HighlightOff sx={{ color: red[500] }} />),
  },
];

const filters: FilterGroup<CompanyUser>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "createdAt",
        type: FilterType.date_range,
      },
      {
        name: "updatedAt",
        type: FilterType.date_range,
      },
    ],
  },
  {
    title: "Filter By Data",
    items: [
      {
        name: "companyId",
        label: "Company",
        type: FilterType.selection,
        valueMap: (v) => cached_companies?.find((a) => a.id === v)?.name || "",
      },
      {
        name: "type",
        type: FilterType.selection,
      },

      {
        name: "enabled",
        type: FilterType.selection,
        options: [
          { value: true, label: "Enabled" },
          { value: false, label: "Disabled" },
        ],
      },
    ],
  },
];

let cached_companies: Company[] | undefined;
export default function CompanyUsers() {
  const load = async () => {
    const [_, data] = await Promise.all([
      !cached_companies ? getCompanies(["stores"]).then(({ data }) => (cached_companies = data)) : undefined,
      getCompanyUsers(),
    ]);
    return data;
  };

  useEffect(() => {
    return () => (cached_companies = undefined);
  }, []);

  const importRef = useRef<FormRefEvent<CompanyUser[]>>(null);
  const tableRef = useRef<any>(null);
  const formRef = useRef<FormRefEvent<CompanyUser>>(null);
  const resetPasswordRef = useRef<FormRefEvent<{ password: string }>>(null);

  const onAddOrUpdate = (param?: CompanyUser) => {
    if (param) {
      (param as any).usernameOrEmail = param.email;
    }
    return window.webApp.setTab<CompanyUser>({
      title: `${param ? "Update" : "New"} Company User`,
      element: <CompanyUserForm ref={formRef} defaultValues={param} companies={cached_companies || []} />,
      onOk: async () => {
        if (param && param?.type !== CompanyUserType.root) {
          return;
        }
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        const res = param ? await updateCompanyUser({ ...param, ...value }) : await addCompanyUser(value);
        return res;
      },
      okText: param && param?.type !== CompanyUserType.root ? "Close" : undefined,
      hideCancel: param && param.type !== CompanyUserType.root,
      show: true,
    });
  };

  const onDelete = (param: CompanyUser) => {
    return deleteCompanyUser(param.username, param.companyId);
  };

  const onSearch = async (value: string, original: CompanyUser[]) => {
    if (original) {
      return {
        data: original.filter((a) => filterOnKeys(a, ["name", "email"], value)),
      };
    } else {
      return { data: [] };
    }
  };

  const onResetPassword = (user: CompanyUser) => {
    return window.webApp.toggleDialog({
      open: true,
      title: `Reset Password — ${user.name}`,
      element: <ResetPasswordForm ref={resetPasswordRef} />,
      onOk: async () => {
        if (!resetPasswordRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const { password } = await resetPasswordRef.current.onSubmit();
        await resetCompanyUserPassword({ username: user.username, password, companyId: user.companyId })
        navigator.clipboard.writeText(password);
        window.webApp.showMessage("success", "Password reset successfully and copied to clipboard");
      },
    });
  };

  const onOpenImport = () => {
    return window.webApp.toggleDialog({
      open: true,
      title: "Import Company Users",
      element: <CompanyUserImportForm ref={importRef} companies={cached_companies || []} />,
      onOk: async () => {
        if (!importRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const users = await importRef.current.onSubmit();
        await batchCreateCompanyUsers({ users });
        await sleep(200 * users.length + 3000);
        tableRef?.current.onRefresh?.();
      },
    });
  };

  return (
    <div>
      <div>
        <LoadingButton onClick={onOpenImport}>Import by csv</LoadingButton>
      </div>
      <TableList<CompanyUser>
        headCells={headCells}
        ref={tableRef}
        onAddBtnText="New Company User"
        uid="username"
        onAdd={onAddOrUpdate}
        onSearch={onSearch}
        searchText="Search by name, email"
        onEdit={onAddOrUpdate}
        onDelete={onDelete}
        onLoad={load}
        filters={filters}
        actions={(row) => [
          {
            label: "Reset Password",
            icon: <LockReset />,
            onClick: () => { onResetPassword(row); },
          },
        ]}
      />
    </div>
  );
}
